'use client';

import { useEffect, useState } from 'react';
import { Eye, EyeOff, Lock, LockKeyhole, Mail } from 'lucide-react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { apiRoutes, getApiUrl } from '@/constants/api';
import { useAuth } from '@/contexts/auth-context';
import {
  NetworkError,
  RateLimitError,
  ServerError,
} from '@/lib/api/errors/api-errors';
import { NotAuthenticatedError } from '@/lib/api/errors/not-authenticated-error';
import { clientApiFetcher } from '@/lib/api/fetcher/client';
import { cn } from '@/lib/utils';
import { User } from '@/types/auth';

import { GoogleButton } from './google-button';
import { LinkedinButton } from './linkedin-button';

export function LoginForm({
  className,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  hideSignupLink = false,
  ...props
}: React.ComponentPropsWithoutRef<'div'> & {
  hideSignupLink?: boolean;
}) {
  const { mutate } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [modalError, setModalError] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();

  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam) {
      setError(decodeURIComponent(errorParam));
    }
  }, [searchParams]);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleLoginFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setLoading(true);
    setError('');
    setModalError('');

    clientApiFetcher<{ message: string; user: User }>(
      getApiUrl(apiRoutes.auth.login),
      {
        skipRefresh: true,
        method: 'POST',
        body: JSON.stringify({
          identifier: email,
          password,
        }),
      },
    )
      .then(async ({ user, message }) => {
        await mutate({ user, message }, false);

        if (!user.isVerified) {
          router.push('/auth/signup/verify-email');
        }

        if (!user.isOnboarded) {
          router.push('/onboarding');
        }

        if (searchParams.get('callbackUrl')) {
          router.push(searchParams.get('callbackUrl')!);
        } else {
          router.push('/feed');
        }
      })
      .catch((err) => {
        if (err instanceof RateLimitError) {
          setModalError(
            `Too many incorrect attempts. 
            Your account is temporarily locked to keep it secure. 
            Please try again after 10 minutes.`,
          );
          setErrorModalOpen(true);
        } else if (err instanceof ServerError) {
          setError('An unexpected error occurred. Please try again later.');
        } else if (err instanceof NetworkError) {
          setError('Network error. Please check your internet connection.');
        } else if (err instanceof NotAuthenticatedError) {
          setError(
            err.message ||
              'Email address or password incorrect. Please try again.',
          );
        } else {
          setError(err.message || 'An unexpected error occurred');
        }

        setLoading(false);
      });
  };

  const handleModalOpen = (open: boolean) => {
    if (!open) {
      setError('');
      setModalError('');
    }
    setErrorModalOpen(open);
  };

  return (
    <>
      <div className={cn('space-y-4', className)} {...props}>
        <form onSubmit={handleLoginFormSubmit} className='space-y-4'>
          <div className='space-y-2'>
            <div className='relative'>
              <Mail className='absolute left-3 top-3 size-4 text-brand-green' />
              <Input
                type='email'
                placeholder='Email address'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className='border-brand-green pl-10 focus:border-brand-green focus:ring-brand-green'
                required
              />
            </div>
            <div className='relative'>
              <Lock className='absolute left-3 top-3 size-4 text-brand-green' />
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder='Password'
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className='border-brand-green px-10 focus:border-brand-green focus:ring-brand-green'
                required
              />
              <button
                onClick={togglePasswordVisibility}
                type='button'
                className='absolute right-3 top-3 text-gray-400 hover:text-gray-600'
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div className='flex justify-end'>
            <Link
              href='/auth/forgot-password'
              className='text-sm text-brand-green transition-colors hover:text-brand-base'
            >
              Forgot Password?
            </Link>
          </div>
          {error && <div className='text-sm text-red-500'>{error}</div>}
          <Button
            type='submit'
            variant='default'
            disabled={loading}
            className='w-full'
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
          <div className='relative py-2'>
            <div className='absolute inset-0 flex items-center'>
              <span className='w-full border-t border-brand-green' />
            </div>
            <div className='relative flex justify-center text-xs uppercase'>
              <span className='bg-white px-2 text-brand-base'>OR</span>
            </div>
          </div>
          <GoogleButton
            text='sign-in'
            callbackUrl={searchParams.get('callbackUrl') || undefined}
          />
          <LinkedinButton
            text='sign-in'
            callbackUrl={searchParams.get('callbackUrl') || undefined}
          />
        </form>
      </div>
      <Dialog onOpenChange={handleModalOpen} open={errorModalOpen}>
        <DialogContent className='px-12 py-8 sm:max-w-[425px]'>
          <DialogHeader>
            <DialogTitle className='flex items-center justify-center p-2'>
              <LockKeyhole className='size-10 text-red-700' />
            </DialogTitle>
            <DialogDescription className='whitespace-pre-line text-center text-lg font-medium text-red-700'>
              {modalError}
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  );
}
